/**
 * bundle-upsell.js
 * Shopify Cart API Integration für Bundle-Kacheln
 * Version: 2.0 — Option B: Handle + Größen-Option
 *
 * Einbinden in theme.liquid (vor </body>):
 * <script src="{{ 'bundle-upsell.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  // ── Hilfsfunktionen ────────────────────────────────────────────────

  /** Formatiert einen Float als deutschen Preis: 12.90 → "12,90 €" */
  function formatPrice(amount) {
    return parseFloat(amount).toFixed(2).replace('.', ',') + '\u202f€';
  }

  /** Zeigt einen temporären Toast unten im Viewport */
  function showToast(message, duration = 3000) {
    let toast = document.querySelector('.bundle-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'bundle-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, duration);
  }

  // ── Varianten-Lookup ───────────────────────────────────────────────

  /**
   * Holt alle Varianten eines Produkts via Handle.
   * Gibt das komplette Shopify-Produkt-Objekt zurück.
   *
   * @param {string} handle  — z.B. "orangen-oel"
   * @returns {Promise<object>}
   */
  async function fetchProduct(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error(`Produkt "${handle}" nicht gefunden.`);
    return response.json();
  }

  /**
   * Findet die richtige Variante für einen gewünschten Optionswert.
   *
   * Logik:
   * 1. Wenn option angegeben: suche Variante, deren options-Array den
   *    Wert enthält (case-insensitiv). Nimm die erste verfügbare.
   * 2. Wenn option leer/null: nimm die erste verfügbare Variante.
   * 3. Fallback: erste Variante überhaupt (auch wenn nicht verfügbar),
   *    damit der Cart-Request nicht lautlos fehlschlägt.
   *
   * @param {object} product   — Shopify-Produkt-Objekt
   * @param {string|null} option — z.B. "10ml", "50ml", null
   * @returns {{ variantId: number, title: string, available: boolean }}
   */
  function resolveVariant(product, option) {
    const variants = product.variants;

    if (option) {
      const needle = option.trim().toLowerCase();

      // Exakter Match bevorzugen, dann case-insensitiv
      const match =
        variants.find(v =>
          v.available &&
          v.options.some(o => o.toLowerCase() === needle)
        ) ||
        variants.find(v =>
          v.options.some(o => o.toLowerCase() === needle)
        );

      if (match) {
        return { variantId: match.id, title: match.title, available: match.available };
      }

      // Option nicht gefunden → Warnung in Konsole, Fallback
      console.warn(
        `[bundle-upsell] Option "${option}" nicht gefunden bei "${product.handle}". ` +
        `Verfügbare Optionen: ${variants.map(v => v.options.join(' / ')).join(' | ')}. ` +
        `Fallback auf erste verfügbare Variante.`
      );
    }

    // Keine Option oder kein Match: erste verfügbare
    const fallback = variants.find(v => v.available) || variants[0];
    return { variantId: fallback.id, title: fallback.title, available: fallback.available };
  }

  // ── Cart API ───────────────────────────────────────────────────────

  /**
   * Löst alle Produkte auf und fügt die richtigen Varianten in den Cart.
   *
   * @param {Array<{handle: string, option: string|null}>} products
   * @returns {Promise<object>}
   */
  async function addBundleToCart(products) {
    // Alle Produkte parallel laden
    const productDataList = await Promise.all(
      products.map(p =>
        fetchProduct(p.handle).catch(err => {
          console.error(`[bundle-upsell] Fehler beim Laden von "${p.handle}":`, err);
          return null;
        })
      )
    );

    // Für jedes Produkt die richtige Variante matchen
    const items = productDataList
      .map((productData, i) => {
        if (!productData) return null;
        const { variantId, title, available } = resolveVariant(productData, products[i].option);

        if (!available) {
          console.warn(`[bundle-upsell] Variante "${title}" ist nicht verfügbar.`);
        }

        return { id: variantId, quantity: 1 };
      })
      .filter(Boolean);

    if (items.length === 0) {
      throw new Error('Keine Varianten gefunden. Bitte Handles und Optionen im Metafeld prüfen.');
    }

    // Alle auf einmal in den Cart
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.description || 'Fehler beim Hinzufügen zum Warenkorb.');
    }

    return response.json();
  }

  // ── UI-Logik ──────────────────────────────────────────────────────

  function initBundleUpsell(wrapper) {
    const atcBlock   = wrapper.querySelector('.bundle-atc');
    const atcBtn     = wrapper.querySelector('.btn-bundle-atc');
    const backBtn    = wrapper.querySelector('.bundle-atc__back');
    const savingSpan = wrapper.querySelector('.bundle-atc__saving-amount');
    const boptAll    = wrapper.querySelectorAll('.bopt');

    let selectedIndex = -1;

    function selectBundle(index) {
      selectedIndex = selectedIndex === index ? -1 : index;

      boptAll.forEach((el, i) => {
        const active = i === selectedIndex;
        el.classList.toggle('is-selected', active);
        el.setAttribute('aria-pressed', String(active));

        const productsEl = el.querySelector('.bproducts');
        if (productsEl) productsEl.setAttribute('aria-hidden', String(!active));
      });

      if (selectedIndex === -1) {
        atcBlock.classList.remove('is-visible');
        atcBtn.disabled = true;
      } else {
        const activeBopt = boptAll[selectedIndex];
        savingSpan.textContent = formatPrice(activeBopt.dataset.saving);
        atcBlock.classList.add('is-visible');
        atcBtn.disabled = false;
      }
    }

    // Klick & Keyboard
    boptAll.forEach((el, i) => {
      el.addEventListener('click', () => selectBundle(i));
      el.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectBundle(i);
        }
      });
    });

    if (backBtn) backBtn.addEventListener('click', () => selectBundle(-1));

    // ATC-Button
    if (atcBtn) {
      atcBtn.addEventListener('click', async () => {
        if (selectedIndex === -1) return;

        const activeBopt = boptAll[selectedIndex];

        // data-products enthält JSON: [{"handle":"orangen-oel","option":"10ml"}, ...]
        let products;
        try {
          products = JSON.parse(activeBopt.dataset.products || '[]');
        } catch (e) {
          showToast('Konfigurationsfehler — bitte Metafeld prüfen.', 5000);
          return;
        }

        if (products.length === 0) {
          showToast('Keine Produkte im Bundle konfiguriert.', 5000);
          return;
        }

        atcBtn.classList.add('is-loading');
        atcBtn.textContent = 'Wird hinzugefügt…';
        atcBtn.disabled = true;

        try {
          await addBundleToCart(products);

          // Warenkorb-Drawer öffnen (Theme-abhängig)
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:open'));
          const cartIcon = document.getElementById('cart-icon-bubble');
          if (cartIcon) cartIcon.click();

          showToast('✓ Bundle wurde hinzugefügt!');
          selectBundle(-1);

        } catch (err) {
          console.error('[bundle-upsell]', err);
          showToast('Fehler: ' + err.message, 5000);
        } finally {
          atcBtn.classList.remove('is-loading');
          atcBtn.textContent = 'Bundle in den Warenkorb';
          atcBtn.disabled = false;
        }
      });
    }
  }

  // ── Initialisierung ───────────────────────────────────────────────

  document.querySelectorAll('.bundle-upsell').forEach(initBundleUpsell);

})();